"use client";
// Ofertas emergentes del sitio: una a la vez, con sus condiciones a la vista y sin tapar la compra. Se activan desde Panel › Ofertas.
import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { EVENTO_CARRITO, leerCarrito } from "@/lib/carrito";
import { puedeMostrar } from "@/lib/ofertas";
import { Img } from "@/components/Img";

type Oferta = { id: string; titulo: string; texto: string; boton: string; enlace: string | null; codigo: string | null; imagen: string | null; condiciones: string; disparador: string; segundos: number; minimoTours: number | null; frecuencia: string };
const paginaDe = (ruta: string) => {
  const p = ruta.replace(/^\/(es|en)/, "").split("/").filter(Boolean);
  if (!p.length) return "inicio";
  if (p[0] === "tours") return p.length > 1 ? "tour" : "tours";
  return ["carrito", "cabanas", "restaurantes", "caballos", "bar", "eventos"].includes(p[0]) ? p[0] : "otra";
};
const guardado = (k: string) => { try { return Number(localStorage.getItem(k)) || null; } catch { return null; } };
const cerradas = (id: string) => { try { return sessionStorage.getItem("sierra-oferta-" + id) === "1"; } catch { return false; } };
const evento = (id: string, tipo: "vista" | "clic") => { void fetch("/api/ofertas/evento", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ id, tipo }), keepalive: true }).catch(() => undefined); };

export function OfertasEmergentes({ idioma }: { idioma: "es" | "en" }) {
  const ruta = usePathname() ?? "", pagina = paginaDe(ruta), es = idioma === "es";
  const [visible, setVisible] = useState<Oferta | null>(null), [copiado, setCopiado] = useState(false);
  const mostradas = useRef(new Set<string>());

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_EXPORT === "1" || pagina === "otra") return;
    let vivo = true; const limpiezas: (() => void)[] = [];
    const mostrar = (o: Oferta) => {
      if (!vivo || mostradas.current.has(o.id) || document.querySelector("[role=dialog][aria-modal=true]")) return;
      if (!puedeMostrar(o.frecuencia, guardado("sierra-oferta-ult-" + o.id), Date.now(), cerradas(o.id))) return;
      mostradas.current.add(o.id); setVisible(o); evento(o.id, "vista");
      try { localStorage.setItem("sierra-oferta-ult-" + o.id, String(Date.now())); } catch { /* sin almacenamiento */ }
    };
    fetch(`/api/ofertas/publicas?pagina=${pagina}`).then((r) => (r.ok ? r.json() : [])).then((lista: Oferta[]) => {
      if (!vivo || !Array.isArray(lista)) return;
      for (const o of lista) {
        if (o.disparador === "entrar") {
          // No se abre encima del hero ni del buscador: espera a que la persona baje de la portada, o 15 s.
          const f = () => { if (window.scrollY > window.innerHeight * 0.7) mostrar(o); };
          const t = setTimeout(() => mostrar(o), 15000);
          f(); window.addEventListener("scroll", f, { passive: true });
          limpiezas.push(() => { clearTimeout(t); window.removeEventListener("scroll", f); });
        }
        else if (o.disparador === "tiempo") {
          // Pasado el tiempo, solo sale si la persona ya bajó de la portada; si no, sale en cuanto baje.
          let cumplido = false;
          const f = () => { if (cumplido && window.scrollY > window.innerHeight * 0.7) mostrar(o); };
          const t = setTimeout(() => { cumplido = true; f(); }, Math.max(1, o.segundos) * 1000);
          window.addEventListener("scroll", f, { passive: true });
          limpiezas.push(() => { clearTimeout(t); window.removeEventListener("scroll", f); });
        }
        else if (o.disparador === "salida") { const f = (e: MouseEvent) => { if (e.clientY <= 0) mostrar(o); }; document.addEventListener("mouseout", f); limpiezas.push(() => document.removeEventListener("mouseout", f)); }
        else if (o.disparador === "carrito") { const f = () => { if (new Set(leerCarrito().map((l) => l.slug)).size >= (o.minimoTours ?? 1)) mostrar(o); }; f(); window.addEventListener(EVENTO_CARRITO, f); limpiezas.push(() => window.removeEventListener(EVENTO_CARRITO, f)); }
      }
    }).catch(() => undefined);
    return () => { vivo = false; limpiezas.forEach((l) => l()); };
  }, [pagina]);

  useEffect(() => {
    if (!visible) return;
    const tecla = (e: KeyboardEvent) => { if (e.key === "Escape") cerrar(); };
    document.addEventListener("keydown", tecla); return () => document.removeEventListener("keydown", tecla);
  });
  function cerrar() { if (visible) { try { sessionStorage.setItem("sierra-oferta-" + visible.id, "1"); } catch { /* sin almacenamiento */ } } setVisible(null); setCopiado(false); }
  if (!visible) return null;
  const o = visible;
  return (
    <aside className={`oferta${o.imagen ? " con-imagen" : ""}`} role="dialog" aria-modal="false" aria-labelledby="oferta-t">
        <button type="button" className="oferta-x" aria-label={es ? "Cerrar" : "Close"} onClick={cerrar}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg></button>
        {o.imagen && <div className="oferta-img"><Img src={o.imagen} alt="" /><span className="oferta-sello">{es ? "Oferta" : "Offer"}</span></div>}
        <div className="oferta-cuerpo">
          {!o.imagen && <span className="oferta-sello">{es ? "Oferta" : "Offer"}</span>}
          <h2 id="oferta-t">{o.titulo}</h2>
          <p>{o.texto}</p>
          {o.codigo && <p className="oferta-codigo">{es ? "Tu código:" : "Your code:"} <code>{o.codigo}</code> <button type="button" onClick={() => { void navigator.clipboard?.writeText(o.codigo!).then(() => setCopiado(true)); }}>{copiado ? (es ? "¡Copiado!" : "Copied!") : (es ? "Copiar" : "Copy")}</button></p>}
          <div className="oferta-acciones">
            {o.enlace && <a className="boton" href={o.enlace} onClick={() => evento(o.id, "clic")}>{o.boton}</a>}
            <button type="button" className="oferta-no" onClick={cerrar}>{es ? "Ahora no" : "Not now"}</button>
          </div>
          {o.condiciones && <small className="oferta-cond">{o.condiciones}</small>}
        </div>
    </aside>
  );
}
